import LanguageSelect from "app/components/LanguageSelector/LanguageSelect";
import { LanguageSelectionHelper } from "app/helpers/languageSelectionHelper";
import { OddsType, ResponsiveScreenSizes } from "app/utils/const";
import isPublicView from "app/utils/publicViewUtils";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import Media from "react-media";
import { connect, useDispatch } from "react-redux";
import { SessionHelper } from "../../helpers/sessionHelper";
import type { UserType } from "../../types/userType";
import "./FinanceSummary.scss";
const CurrencyNumber: React.LazyExoticComponent<any> = lazy(
  () => import("../../components/CurrencyNumber/CurrencyNumber")
);

type Props = {
  loadUser: (...args: Array<any>) => any;
  user: UserType;
  pollingAccount: boolean;
  isIdle: boolean;
  sessionView: string;
  onLanguageChangeRequest: (...args: Array<any>) => any;
};

const FinanceSummary: React.FC<Props> = ({
  loadUser,
  user,
  isIdle,
  sessionView,
  onLanguageChangeRequest,
}) => {
  const dispatch = useDispatch();
  const [intervalId, setIntervalId] = useState<any>(0);
  const [intervalStarted, setIntervalStarted] = useState(false);
  const [selectedSessionView, setSelectedSessionView] = useState(sessionView);
  const [selectedOddsType, setSelectedOddsType] = useState(OddsType.DECIMAL);

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    if (!intervalStarted) {
      const id = setInterval(() => {
        if (isIdle) return;
        loadUser();
      }, 60000);
      setIntervalId(id);
      setIntervalStarted(true);
    }
    return () => {
      clearInterval(intervalId);
      setIntervalStarted(false);
    };
  }, [isIdle]);

  useEffect(() => {
    if (sessionView === "asian" && selectedSessionView === "") {
      SessionHelper.isAsianRouteAndSessionView() &&
        setSelectedSessionView(sessionView);
    }
  }, [sessionView]);

  useEffect(() => {
    if (user.preferences !== undefined) {
      if (user.preferences.oddsType !== selectedOddsType) {
        setSelectedOddsType(user.preferences.oddsType);
      }
    }
  }, [user]);

  if (user == null || user.balance == null) {
    return null;
  }

  const { balance, credit, exposure, amountAvailable } = user.balance;

  return (
    <div
      className={`finance-summary ${
        SessionHelper.isAsianRouteAndSessionView()
          ? "asian-finance-summary"
          : "euro-finance-summary"
      } ${isPublicView() ? "public-view" : ""}`}
    >
      <Media
        query={`(max-width: ${ResponsiveScreenSizes.mobile})`}
        render={() => (
          <div className={`public-view-mobile`}>
            {isPublicView() && (
              <div className="language__input--row">
                <LanguageSelect
                  onLanguageChangeRequest={onLanguageChangeRequest}
                  locale={LanguageSelectionHelper.checkSelectedLanguage()}
                  small={true}
                />
              </div>
            )}
          </div>
        )}
      />

      <Suspense fallback={<div className="fallback">loading...</div>}>
        <div
          className={`finance-summary ${
            SessionHelper.isAsianRouteAndSessionView()
              ? "asian-finance-summary"
              : "euro-finance-summary"
          }`}
        >
          <div className="finance-summary__col">
            <div className="finance-summary__col__title">
              <FormattedMessage tagName="span" id="users-available" />
            </div>
            <CurrencyNumber
              value={amountAvailable}
              hideDecimalIfZeros={true}
              narrowSymbol={true}
              QAID="finance-summary__available"
            />
          </div>
          <div className="finance-summary__col">
            <div className="finance-summary__col__title">
              <FormattedMessage
                tagName="span"
                id={
                  SessionHelper.isAsianSessionView()
                    ? "users-cash-balance"
                    : "users-balance"
                }
              />
            </div>
            <CurrencyNumber
              value={balance}
              hideDecimalIfZeros={true}
              narrowSymbol={true}
              QAID="finance-summary__balance"
            />
          </div>
          <div className="finance-summary__col">
            <div className="finance-summary__col__title">
              <FormattedMessage
                tagName="span"
                id={
                  SessionHelper.isAsianSessionView()
                    ? "users-bet-credit"
                    : "users-credit"
                }
              />
            </div>
            <CurrencyNumber
              value={credit}
              minimumFractionDigits={0}
              narrowSymbol={true}
              QAID="finance-summary__credit"
            />
          </div>
          <div className="finance-summary__col">
            <div className="finance-summary__col__title">
              <FormattedMessage tagName="span" id="users-exposure" />
            </div>
            <CurrencyNumber
              value={exposure}
              hideDecimalIfZeros={true}
              narrowSymbol={true}
              QAID="finance-summary__exposure"
            />
          </div>
        </div>
      </Suspense>
    </div>
  );
};

const mapStateToProps = state => ({
  user: state.userModel.user,
  isIdle: state.sessionModel.isIdle,
  sessionView: state.sessionModel.view,
  oddsOptions: state.competitionListModel.oddsOptions,
  competitionList: state.competitionListModel.competitionList,
  competitionListOffset: state.competitionListModel.competitionListOffset,
});

const mapDispatchToProps = (dispatch: any) => ({
  loadUser: () => {
    dispatch.userModel.loadUser();
  },
  onLanguageChangeRequest: (lang: string) => {
    dispatch.profileModel.onLanguageChange(lang);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(FinanceSummary);
